import * as Sentry from '@sentry/react'
import { apiErrorLevel, isLocalDevOutage, observeBrowserFailures, prepareTelemetryEvent, sanitizeTelemetry } from '@redditwins/shared'

/**
 * Client observability: GlitchTip for exceptions, and every client-side
 * problem (API failures, JS errors, unhandled rejections, slow pages) batched
 * to the API diagnostic trail (`POST /api/v1/diagnostics/client`) with the
 * request id of the failing call, so server and browser traces join up.
 */
const dsn = import.meta.env.VITE_GLITCHTIP_DSN as string | undefined
let glitchtip = false
let initialized = false
let activeWorkspaceId: number | null = null

export function initMonitoring() {
  if (initialized) return
  initialized = true
  if (dsn && (import.meta.env.PROD || import.meta.env.VITE_GLITCHTIP_ALLOW_NON_PRODUCTION === 'true')) {
    Sentry.init({
      dsn,
      environment: (import.meta.env.VITE_GLITCHTIP_ENVIRONMENT as string | undefined) ?? import.meta.env.MODE,
      release: (import.meta.env.VITE_SOURCE_COMMIT as string | undefined) || undefined,
      // GlitchTip drops trace-enriched envelopes: errors only.
      tracesSampleRate: 0,
      sampleRate: 1,
      sendDefaultPii: false,
      maxBreadcrumbs: 100,
      integrations: (defaults) => [
        ...defaults.filter((i) => !['InboundFilters', 'EventFilters', 'Dedupe', 'GlobalHandlers'].includes(i.name)),
        Sentry.captureConsoleIntegration({ levels: ['warn', 'error', 'assert'] }),
      ],
      // A dev server opted into reporting still drops its own local API restarts and HMR errors.
      beforeSend: (event) => (import.meta.env.DEV && isLocalDevOutage(event) ? null : prepareTelemetryEvent(event)),
      beforeBreadcrumb: sanitizeTelemetry,
    })
    Sentry.setTag('surface', 'frontend')
    glitchtip = true
  }
  window.addEventListener('error', (event) => {
    if (!event.message) return
    report('window.error', 'error', event.message, { source: event.filename, line: event.lineno, column: event.colno }, null, event.error ?? event.message)
  })
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason as unknown
    report('window.unhandled_rejection', 'error', reason instanceof Error ? reason.message : String(reason), {}, null, reason)
  })
  observeBrowserFailures(({ name, error, data }) => report(name, 'error', error instanceof Error ? error.message : String(error), data, null, error))
  window.addEventListener('pagehide', () => flush())
}

export function capture(error: unknown, context: Record<string, string> = {}, extra: Record<string, unknown> = {}, level: 'warning' | 'error' = 'error') {
  if (!glitchtip) return
  Sentry.withScope((scope) => {
    for (const [key, value] of Object.entries(context)) scope.setTag(key, value)
    scope.setExtras(extra)
    scope.setLevel(level)
    Sentry.captureException(error)
  })
}

export function identify(user: { id: number; email: string } | null, workspaceId: number | null) {
  if (workspaceId !== activeWorkspaceId) flush()
  activeWorkspaceId = workspaceId
  if (!glitchtip) return
  Sentry.setUser(user ? { id: String(user.id) } : null)
  Sentry.setTag('workspace_id', workspaceId ? String(workspaceId) : 'none')
}

type ClientEvent = {
  name: string
  level: 'debug' | 'info' | 'warn' | 'error'
  message?: string | null
  requestId?: string | null
  path?: string | null
  data?: Record<string, unknown>
}

let queue: ClientEvent[] = []
let timer: number | null = null

export function report(name: string, level: ClientEvent['level'], message?: string | null, data: Record<string, unknown> = {}, requestId?: string | null, error?: unknown) {
  if (level === 'warn' || level === 'error') {
    capture(error ?? new Error(message ?? name), { operation: name, ...(requestId ? { request_id: requestId } : {}) }, data, level === 'warn' ? 'warning' : 'error')
  } else if (glitchtip) {
    Sentry.addBreadcrumb({ category: name, message: message ?? undefined, data: sanitizeTelemetry(data), level })
  }
  queue.push(sanitizeTelemetry({ name: name.slice(0, 96), level, message: message?.slice(0, 4000) ?? null, requestId: requestId ?? null, path: window.location.pathname, data }))
  if (queue.length >= 20) flush()
  else timer ??= window.setTimeout(() => flush(), 3000)
}

function flush() {
  if (timer) {
    window.clearTimeout(timer)
    timer = null
  }
  if (queue.length === 0) return
  const events = queue.splice(0, 50)
  const body = JSON.stringify({ events })
  const headers: Record<string, string> = { 'content-type': 'application/json' }
  if (activeWorkspaceId) headers['x-workspace-id'] = String(activeWorkspaceId)
  void fetch('/api/v1/diagnostics/client', { method: 'POST', headers, body, credentials: 'include', keepalive: true }).then((response) => {
    if (!response.ok) capture(new Error(`Client diagnostics rejected (${response.status})`), { operation: 'diagnostics.delivery', status: String(response.status) })
  }).catch((error: unknown) => capture(error, { operation: 'diagnostics.delivery' }))
  if (queue.length) timer ??= window.setTimeout(() => flush(), 3000)
}

const reportedApiErrors = new WeakSet<object>()

/** API failure from ky/Tuyau: status, route, request id. */
export function reportApiError(error: unknown, route: string) {
  if (error instanceof Error && error.name === 'AbortError') return
  const maybe = error as { response?: { headers?: Headers; status?: number }; rawResponse?: Response; rawRequest?: Request; status?: number; message?: string } | null
  if (maybe?.rawRequest?.signal.aborted) return
  if (error !== null && typeof error === 'object') {
    if (reportedApiErrors.has(error)) return
    reportedApiErrors.add(error)
  }
  const status = maybe?.response?.status ?? maybe?.status ?? 0
  const requestId = maybe?.rawResponse?.headers.get('x-request-id') ?? maybe?.rawRequest?.headers.get('x-request-id') ?? maybe?.response?.headers?.get?.('x-request-id') ?? null
  report('api.error', apiErrorLevel(route, status), maybe?.message ?? 'API error', { route, status }, requestId, error)
}
